import { Receipt, Timer } from 'lucide-react';
import { type ComponentType, lazy } from 'react';
import type { RouteObject } from 'react-router';
import type { ModuleDefinition } from '../../app/modules';
import { useTimeAvailable } from './api';

/**
 * Облік часу (М6, рівень Clockify): трекер, табель тижня, погодження й закриття періодів, звіти, розклад
 * і завантаження, відсутності, витрати, бюджети й ставки, налаштування обліку.
 * У задачі — ▶/■ у шапці картки й рядок «Час»; у верхній панелі — чип запущеного таймера.
 * Сторінки й віджети завантажуються ліниво — без циклічних імпортів із реєстром модулів.
 */
const TaskTimerButton = lazy(() => import('./TaskTimer').then((m) => ({ default: m.TaskTimerButton })));
const TaskTimeRow = lazy(() => import('./TaskTimer').then((m) => ({ default: m.TaskTimeRow })));
const RunningTimerChip = lazy(() => import('./TaskTimer').then((m) => ({ default: m.RunningTimerChip })));
const TaskExpensesPanel = lazy(() => import('./TaskExpenses').then((m) => ({ default: m.TaskExpensesPanel })));

const page = (load: () => Promise<{ default: ComponentType }>): Pick<RouteObject, 'lazy'> => ({
  lazy: async () => ({ Component: (await load()).default }),
});

const timeModule: ModuleDefinition = {
  key: 'time',
  label: 'Облік часу',
  icon: Timer,
  basePath: '/time',
  scope: 'time:read',
  // Модуль доступний, коли сервер відповідає на GET /api/v1/time/timer (інакше елементів обліку часу немає).
  useAccess: useTimeAvailable,
  order: 10,
  routes: [
    {
      ...page(() => import('./TimeLayout')),
      children: [
        { index: true, ...page(() => import('./pages/TrackerPage')) },
        { path: 'timesheet', ...page(() => import('./pages/TimesheetPage')) },
        { path: 'approvals', ...page(() => import('./pages/ApprovalsPage')) },
        { path: 'reports', ...page(() => import('./pages/ReportsPage')) },
        { path: 'schedule', ...page(() => import('./pages/SchedulePage')) },
        { path: 'absences', ...page(() => import('./pages/AbsencesPage')) },
        { path: 'expenses', ...page(() => import('./pages/ExpensesPage')) },
        { path: 'budgets', ...page(() => import('./pages/BudgetsPage')) },
        { path: 'settings', ...page(() => import('./pages/SettingsPage')) },
      ],
    },
  ],
  task: {
    Toolbar: TaskTimerButton,
    Fields: TaskTimeRow,
    // Вкладку «Фінанси» бачать лише ролі з finance:read; витрати за задачею — її розділ.
    panel: { title: 'Витрати й запити коштів', icon: Receipt, Component: TaskExpensesPanel, tab: 'finance' },
  },
  shell: { Topbar: RunningTimerChip },
};

export default timeModule;
